import { useEffect, useMemo } from 'react'

import Modal from 'react-modal'
import { useStopwatch } from 'react-timer-hook'

import { LiquiditySource, ResultCode, StepNames } from '@src/enums'
import ProcessingSwap from '@src/pages/Exchange/components/SwapInProgressFlow/ProcessingSwap'
import SwapCompleted from '@src/pages/Exchange/components/SwapInProgressFlow/SwapCompleted'
import SwapFailed from '@src/pages/Exchange/components/SwapInProgressFlow/SwapFailed'
import TransactionSummary from '@src/pages/Exchange/components/SwapInProgressFlow/TransactionSummary'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useFailOnTimeout } from '@src/pages/Exchange/hooks/useFailOnTimeout'
import { IFee } from '@src/types/fee'
import { createContextLogger } from '@src/utils/logger'
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { useHistoryStore } from '@src/zustand_stores/history.store'

const swapLogger = createContextLogger('SwapInProgressFlow')

interface ISwapInProgressFlowProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    currentStep: StepNames
    result?: {
        status: ResultCode
        sourceTxHash?: string
        destinationTxHash?: string
        message?: string
        transactionETA?: number
    }
    fees: IFee[]
    destinationAddress?: string
    miles?: number
    liquiditySource: LiquiditySource
}

const SwapInProgressFlow: React.FC<ISwapInProgressFlowProps> = ({
    isOpen,
    onClose,
    onConfirm: parentOnConfirm,
    currentStep = StepNames.UNSTARTED,
    result = undefined,
    fees,
    destinationAddress,
    miles,
    liquiditySource,
}: ISwapInProgressFlowProps) => {
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const { exchangeId } = useApplicationStore()
    const stopwatch = useStopwatch({ autoStart: false })

    // Internally listens for changes in swap status.
    useFailOnTimeout()

    /**
     * * * * * * * * * * * * * * * * * * * * * NEW EXCHANGE DATA CONTEXT * * * * * * * * * * * * * * *
     */
    const { sellData, buyData } = useExchangeDataContext()
    /**
     * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
     */

    // Wrap the onConfirm in a Promise
    const handleConfirm = () => {
        return new Promise<void>((resolve, reject) => {
            try {
                parentOnConfirm()
                resolve()
            } catch (error: any) {
                handleTransactionError(error)
                reject(error)
            }
        }).catch((error: any) => {
            handleTransactionError(error)
            throw error
        })
    }

    // Add helper function to handle transaction errors
    const handleTransactionError = (error: any) => {
        swapLogger.error('Transaction error:', { error })
        swapLogger.info('Current step when error occurred:', { currentStep })

        const isUserRejection =
            error?.message?.includes('rejected') ||
            error?.message?.includes('user denied') ||
            error?.message?.includes('user cancelled') ||
            error?.message?.includes('User rejected')

        // Keep track of the current step before it was rejected
        const currentPendingStep = currentStep
        swapLogger.info('Current pending step:', { currentPendingStep })

        // Calculate the step to revert to
        const revertToStep = (() => {
            switch (currentPendingStep) {
                case StepNames.DEPOSIT_WETH_PENDING:
                    return StepNames.DEPOSIT_WETH
                case StepNames.APPROVE_TOKENS_PENDING:
                    return StepNames.APPROVE_TOKENS
                case StepNames.CONFIRM_SWAP_PENDING:
                    return StepNames.CONFIRM_SWAP
                case StepNames.UNSTARTED:
                    return StepNames.DEPOSIT_WETH
                default:
                    return currentPendingStep
            }
        })()

        swapLogger.info('Reverting to step:', { revertToStep })

        // Update the history with appropriate error state

        updateHistory(exchangeId, {
            currentStep: revertToStep,
            result: {
                status: isUserRejection ? ResultCode.pending : ResultCode.failure,
                message: isUserRejection
                    ? 'Transaction was rejected. Please try again.'
                    : 'Transaction failed. Please try again.',
            },
        })
    }

    useEffect(() => {
        if (currentStep === StepNames.MATCH_CREATED) {
            stopwatch.reset()
            stopwatch.start()
        } else if (result?.status === ResultCode.success || result?.status === ResultCode.failure) {
            stopwatch.pause()
        }
    }, [currentStep, result?.status])

    const isCCTPTransfer = useMemo(() => {
        return (
            sellData.token?.symbol === 'USDC' &&
            buyData.token?.symbol === 'USDC' &&
            sellData.chainId !== buyData.chainId &&
            liquiditySource === LiquiditySource.cctpDirect
        )
    }, [sellData.token?.symbol, buyData.token?.symbol, sellData.chainId, buyData.chainId, liquiditySource])

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            ariaHideApp={false}
            className={`fixed left-1/2 top-1/2 flex max-h-[calc(100vh-90px)] w-[464px] max-w-[calc(100%-24px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden overflow-y-auto outline-none transition-all duration-500`}
            style={{
                overlay: {
                    backgroundColor: 'rgba(0,0,0,.6)',
                },
            }}
            id='swap-progress-modal'
        >
            <div className='space-y-5 rounded-2xl border border-mach-modal-border bg-mach-modal-background px-2 py-7 md:space-y-10'>
                {!currentStep.includes('MATCH') ? (
                    <TransactionSummary
                        isOpen={isOpen}
                        onClose={onClose}
                        onConfirm={handleConfirm}
                        currentStep={currentStep}
                        sellData={sellData}
                        buyData={buyData}
                        fees={fees}
                        destinationAddress={destinationAddress}
                        miles={miles}
                        liquiditySource={liquiditySource}
                        isCCTPTransfer={isCCTPTransfer}
                    />
                ) : (
                    <>
                        {result?.status === ResultCode.success && (
                            <SwapCompleted
                                sellData={sellData}
                                buyData={buyData}
                                result={result}
                                miles={miles}
                                fullTradeTimeTimer={stopwatch}
                            />
                        )}
                        {result?.status === ResultCode.failure && (
                            <SwapFailed
                                sellData={sellData}
                                buyData={buyData}
                                result={result}
                                currentStep={currentStep}
                                fees={fees}
                                destinationAddress={destinationAddress}
                                liquiditySource={liquiditySource}
                            />
                        )}
                        {result?.status === ResultCode.pending && (
                            <ProcessingSwap
                                onClose={onClose}
                                currentStep={currentStep}
                                fees={fees}
                                destinationAddress={destinationAddress}
                                miles={miles}
                                result={result}
                                fullTradeTimeTimer={stopwatch}
                                liquiditySource={liquiditySource}
                                isCCTPTransfer={isCCTPTransfer}
                            />
                        )}
                    </>
                )}
            </div>
        </Modal>
    )
}

export default SwapInProgressFlow
